import React, { useState, useEffect } from "react";
import { useTranslation } from 'react-i18next';
import { Form, Button, Row, Col, Alert } from "react-bootstrap";
import axios from "axios";

// Import the loader from lders
import { grid } from "ldrs";
grid.register();

const Step2 = ({ onNext }) => {
  const { t } = useTranslation();
  const [items, setItems] = useState([]);
  const [selectedItem, setSelectedItem] = useState(""); // Ensure default is an empty string
  const [loading, setLoading] = useState(false); // For loading states
  const [saving, setSaving] = useState(false); // For saving state
  const [alertMessage, setAlertMessage] = useState("");
  const [alertType, setAlertType] = useState(""); // For alert types (danger, success)

  // Fetch industries for dropdown
  const fetchIndustries = async () => {
    setLoading(true); // Start loading spinner
    try {
      const response = await axios.get("/step2-values");
      if (response.data.status) {
        setItems(response.data.step2Values);
      } else {
        setAlertMessage(t('steps.failedToFetchIndustries'));
        setAlertType("danger");
      }
    } catch (error) {
      console.error("Error fetching industries:", error);
      setAlertMessage(t('steps.errorFetchingIndustries'));
      setAlertType("danger");
    } finally {
      setLoading(false); // Stop loading spinner
    }
  };

  // Fetch selected item (prefill from previous step)
  const fetchSelectedItem = async () => {
    setLoading(true); // Start loading spinner
    try {
      const response = await axios.get("/get-step-response", {
        params: { stepNumber: 2 },
      });
      if (response.data.status && response.data.fieldId) {
        setSelectedItem(response.data.fieldId);
      } else {
        setSelectedItem(""); // Reset to default
      }
    } catch (error) {
      console.error("Error fetching selected item:", error);
      setAlertMessage("Error fetching previously selected item.");
      setAlertType("danger");
    } finally {
      setLoading(false); // Stop loading spinner
    }
  };

  // Handle save action (send selected item to backend)
  const handleSave = async () => {
    if (!selectedItem) {

      // alert("Please select an industry before saving.");

      setAlertType("danger");

      return;
    }

    setSaving(true); // Start saving state
    try {
      const response = await axios.post("/save-steps", {
        stepNumber: 2,
        fieldId: selectedItem,
      });

      if (response.data.status) {
        setAlertMessage(t('steps.stepSavedSuccessfully'));
        setAlertType("success");
        onNext(); // Proceed to next step
      } else {
        setAlertMessage(t('steps.failedToSaveStep'));
        setAlertType("danger");
      }
    } catch (error) {
      console.error("Error saving step:", error);
      setAlertMessage(t('steps.errorSavingStep'));
      setAlertType("danger");
    } finally {
      setSaving(false); // Stop saving state
    }
  };

  // Handle form submission
  const handleSubmit = (e) => {
    e.preventDefault();
    handleSave(); // Save and proceed to the next step
  };

  useEffect(() => {
    // Fetch items for dropdown and prefill selected item
    fetchIndustries();
    fetchSelectedItem();
  }, []);

  return (
    <div className="container-fluid">
  <div className="row">
    <div className="col-lg-12 mx-auto mb-2">
      <div className="card p-4 border-0">
        <div className="card-body">
          <h5 className="text-[30px] mb-4 font-semibold leading-[110%] text-[#25324B] tracking-wide">
            {t('steps.selectSector')}
          </h5>

          {/* Bootstrap Alert */}
          {alertMessage && (
            <Alert variant={alertType} dismissible onClose={() => setAlertMessage("")}>
              {alertMessage}
            </Alert>
          )}

          <Form onSubmit={handleSubmit}>
            {/* Loading Indicator */}
            {loading && (
              <div className="text-center mb-3">
                <l-grid size="60" speed="1.5" color="#2F855A"></l-grid> {/* Green-700 */}
                <p>{t('steps.loadingSectors')}</p>
              </div>
            )}

            <Row className="g-4">
              {/* Display Industry Items */}
              {!loading && items.length > 0 ? (
                items.map((item) => (
                  <Col md={6} lg={4} key={item.id} className="mb-4">
                    <div
                      className={`card h-100 p-3 border rounded-lg cursor-pointer transition-all duration-300 ease-in-out d-flex flex-column align-items-center text-center ${
                        selectedItem === item.id ? "border-green-700 text-white shadow-lg" : "border-gray-300 bg-white hover:shadow-md"
                      }`}
                      style={{
                        backgroundColor: selectedItem === item.id ? "#388E3C" : "white", // Override Bootstrap
                      }}
                      onClick={() => setSelectedItem(item.id)}
                    >
                      <div className="card-body d-flex flex-column align-items-center gap-2">
                        {/* Industry Icon */}
                        <div className="d-flex align-items-center gap-2">
                          <i
                            className={`bi bi-buildings text-xl ${
                              selectedItem === item.id ? "text-white" : "text-gray-500"
                            }`}
                          ></i>
                        </div>
                        <h5 className="card-title font-semibold">{item.industryName}</h5>
                        <p className="card-text">{item.industryDescription}</p>
                      </div>
                    </div>
                  </Col>
                ))
              ) : (
                <p className="text-center">{t('steps.noIndustriesAvailable')}</p>
              )}
            </Row>

            {/* Save Button */}
            <div className="d-flex justify-content-end mt-4">
            <Button
              type="submit"
              className="btn btn-success text-white px-4 text-lg font-semibold transition-all duration-300 hover:bg-green-600"
              style={{
                borderColor: "#2F855A",
                fontSize: "1.25rem", // Equivalent to `text-lg`
              }}
              disabled={saving || !selectedItem}
            >
{saving ? t('answers.saving') : t('steps.next')}
            </Button>
            </div>
          </Form>
        </div>
      </div>
    </div>
  </div>
</div>

  );
};

export default Step2;
